import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Formik } from "formik";
import * as Yup from "yup";
import BeatLoader from "react-spinners/BeatLoader";
import { toast } from "react-toastify";
import {
  portalResendOtp,
  portalVerifyOtp,
} from "../../services/merchant-portal";
import {
  clearOtpChallenge,
  getOtpChallenge,
  setOtpChallenge,
  setPortalTokens,
} from "../../utils/merchant-portal-storage";
import { usePortalAuth } from "../../contexts/PortalAuthContext";
import "./portal.scss";

const schema = Yup.object({
  otp: Yup.string()
    .matches(/^[0-9]{6}$/, "Enter the 6-digit code")
    .required("OTP is required"),
});

const MerchantPortalVerifyOtp = () => {
  const navigate = useNavigate();
  const { markLoggedIn } = usePortalAuth();
  const [{ challenge_id, masked_phone }, setChallenge] = useState(() =>
    getOtpChallenge(),
  );
  const [formError, setFormError] = useState("");
  const [resending, setResending] = useState(false);

  useEffect(() => {
    if (!challenge_id) {
      navigate("/merchant-portal/login", { replace: true });
    }
  }, [challenge_id, navigate]);

  const handleResend = async () => {
    if (!challenge_id || resending) return;
    setResending(true);
    setFormError("");
    try {
      const res = await portalResendOtp(challenge_id);
      if (res.status_code === 200 && res.data?.challenge_id) {
        setOtpChallenge(res.data.challenge_id, masked_phone);
        setChallenge(getOtpChallenge());
        toast.success(res.message || "OTP resent");
      } else {
        toast.error(res.message || "Could not resend OTP");
      }
    } catch (err) {
      toast.error(`${err}`);
    } finally {
      setResending(false);
    }
  };

  if (!challenge_id) {
    return null;
  }

  return (
    <div className="portal-auth-page bg-main-bg dark:bg-main-dark-bg">
      <div className="portal-card portal-auth-card">
        <h1 className="text-xl font-semibold mb-1">Verify OTP</h1>
        <p className="portal-muted mb-6">
          Enter the 6-digit code sent to{" "}
          <strong>{masked_phone || "your WhatsApp"}</strong>.
        </p>
        {formError ? (
          <div className="text-red-500 text-sm mb-4">{formError}</div>
        ) : null}
        <Formik
          initialValues={{ otp: "" }}
          validationSchema={schema}
          onSubmit={async (values, { setSubmitting }) => {
            setFormError("");
            try {
              const res = await portalVerifyOtp(challenge_id, values.otp);
              if (res.status_code === 200 && res.data?.access_token) {
                setPortalTokens(res.data);
                clearOtpChallenge();
                markLoggedIn();
                toast.success(res.message || "Signed in");
                navigate("/merchant-portal/dashboard", { replace: true });
              } else {
                setFormError(res.message || "Invalid OTP");
              }
            } catch (err) {
              setFormError(`${err}`);
            } finally {
              setSubmitting(false);
            }
          }}
        >
          {({
            handleSubmit,
            handleChange,
            values,
            errors,
            touched,
            isSubmitting,
          }) => (
            <form onSubmit={handleSubmit} className="portal-form">
              <div className="field">
                <label htmlFor="otp">One-time code</label>
                <input
                  id="otp"
                  name="otp"
                  inputMode="numeric"
                  maxLength={6}
                  value={values.otp}
                  onChange={handleChange}
                  autoComplete="one-time-code"
                />
                {errors.otp && touched.otp ? (
                  <div className="error">{errors.otp}</div>
                ) : null}
              </div>
              <button
                type="submit"
                className="portal-btn w-full mt-2"
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <BeatLoader size={8} color="#fff" />
                ) : (
                  "Verify and sign in"
                )}
              </button>
            </form>
          )}
        </Formik>
        <div className="flex justify-between items-center mt-4">
          <Link to="/merchant-portal/login" className="text-[#03C9D7] text-sm">
            Start over
          </Link>
          <button
            type="button"
            className="portal-btn portal-btn--secondary text-sm"
            onClick={handleResend}
            disabled={resending}
          >
            {resending ? "Sending…" : "Resend code"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default MerchantPortalVerifyOtp;
